"use client";

import { useState, useEffect } from "react";
import config from "../../config/config";
import Image from "next/image";
import Link from "next/link";
import { getFeaturedImage } from "../../utils/imageUtils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  FaCalendarAlt,
  FaUser,
  FaEye,
  FaShareAlt,
  FaFacebookF,
  FaTwitter,
  FaWhatsapp,
  FaCopy,
  FaHome,
  FaChevronRight,
} from "react-icons/fa";
import { FileText, Download } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

// Import sidebar and post components
import Sidebar from "../sidebar";
import { RelatedPosts } from "./components";

export default function UnifiedPostLayout({ post, children }) {
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [copied, setCopied] = useState(false);

  // Define special post types that should have the page-like layout (no author, date, etc.)
  const specialPostTypes = ["page", "video", "gallery", "members-list"];
  const isSpecialPostType = post && specialPostTypes.includes(post.type);

  // Fetch related posts - skip for special post types
  useEffect(() => {
    const fetchRelatedPosts = async () => {
      try {
        const response = await fetch(
          `${config.NEXT_PUBLIC_API_URL}/api/public/p?type=${post.type}&limit=4&exclude=${post._id}`
        );
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setRelatedPosts(result.data.posts);
          }
        }
      } catch (error) {
        console.error("Error fetching related posts:", error);
      }
    };

    if (post && !isSpecialPostType) {
      fetchRelatedPosts();
    }
  }, [post, isSpecialPostType]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const sharePost = async (platform) => {
    const url = window.location.href;
    const text = `${post.title} - LJPR`;

    switch (platform) {
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            url
          )}`,
          "_blank"
        );
        break;
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            text
          )}&url=${encodeURIComponent(url)}`,
          "_blank"
        );
        break;
      case "whatsapp":
        window.open(
          `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
          "_blank"
        );
        break;
      case "copy":
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          console.error("Failed to copy URL:", err);
        }
        break;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-5">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1 sm:gap-2 text-sm text-muted-foreground mb-4 sm:mb-6 min-w-0">
          <Link
            href="/"
            className="flex items-center gap-1 hover:text-primary transition-colors flex-shrink-0"
          >
            <FaHome className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="text-xs sm:text-sm">Home</span>
          </Link>
          <FaChevronRight className="w-2 h-2 sm:w-3 sm:h-3 flex-shrink-0" />
          {!isSpecialPostType && (
            <>
              <Link
                href={`/p/${post.type}`}
                className="hover:text-primary transition-colors text-xs sm:text-sm flex-shrink-0"
              >
                {post.type.charAt(0).toUpperCase() +
                  post.type.slice(1).replace("-", " ")}
              </Link>
              <FaChevronRight className="w-2 h-2 sm:w-3 sm:h-3 flex-shrink-0" />
            </>
          )}
          <span className="text-xs sm:text-sm font-medium text-foreground truncate min-w-0 flex-1">
            {post.title}
          </span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <article className="w-full overflow-hidden">
              {/* Featured Image - Hide for special post types */}
              {!isSpecialPostType && (
                <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden bg-muted">
                  <Image
                    src={getFeaturedImage(post, "large")}
                    alt={post.title}
                    fill
                    className="object-cover transition-transform duration-300 hover:scale-105"
                    priority
                    sizes="(max-width: 768px) 100vw, (max-width: 1024px) 75vw, 75vw"
                  />
                  {/* Gradient Overlay for better text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
              )}

              {/* Content */}
              <div className="w-full mt-5">
                {/* Categories - Not shown for special post types */}
                {!isSpecialPostType &&
                  post.categories &&
                  post.categories.length > 0 && (
                    <div className="mb-2">
                      <div className="flex flex-wrap gap-2">
                        {post.categories.map((category, index) => (
                          <Badge
                            key={index}
                            variant="secondary"
                            className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                          >
                            {category}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                {/* Fallback to single category if categories array isn't available - Not shown for special post types */}
                {!isSpecialPostType && !post.categories && post.category && (
                  <div className="mb-2">
                    <Badge
                      variant="secondary"
                      className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                    >
                      {post.category}
                    </Badge>
                  </div>
                )}

                {/* Title */}
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2 lg:mb-2 leading-tight tracking-tight">
                  {post.title}
                </h1>

                {/* PDF Download Button for Press Releases */}
                {post.type === "press-release" && post.pdfFile && (
                  <div className="mb-4">
                    <Button
                      asChild
                      className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2"
                    >
                      <a
                        href={post.pdfFile.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={post.pdfFile.filename || "press-release.pdf"}
                      >
                        <FileText className="w-4 h-4" />
                        <Download className="w-4 h-4" />
                        Download PDF
                      </a>
                    </Button>
                    <p className="text-sm text-muted-foreground mt-2">
                      Download the complete press release document (
                      {post.pdfFile.filename || "PDF"})
                    </p>
                  </div>
                )}

                {/* Meta Information */}
                <div className="flex items-center justify-between gap-2 sm:gap-4 text-sm text-muted-foreground mb-6 pb-4 lg:pb-6 border-b border-border">
                  <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
                    {!isSpecialPostType && (
                      <div className="flex items-center gap-1 sm:gap-2">
                        <FaCalendarAlt className="w-3 h-3 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                        <span className="font-medium text-xs sm:text-sm truncate">
                          {formatDate(post.publishDate || post.createdAt)}
                        </span>
                      </div>
                    )}
                    {post.author && !isSpecialPostType && (
                      <div className="flex items-center gap-1 sm:gap-2">
                        <FaUser className="w-3 h-3 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                        <span className="font-medium text-xs sm:text-sm truncate">
                          {post.author}
                        </span>
                      </div>
                    )}
                    {post.views && (
                      <div className="flex items-center gap-1 sm:gap-2">
                        <FaEye className="w-3 h-3 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                        <span className="font-medium text-xs sm:text-sm">
                          {post.views}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Share Button - Hide for special post types */}
                  {!isSpecialPostType && (
                    <div className="flex-shrink-0">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-primary/20 text-primary hover:bg-primary/10 text-xs sm:text-sm px-2 sm:px-3"
                          >
                            <FaShareAlt className="w-2.5 h-2.5 sm:w-4 sm:h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Share</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem
                            onClick={() => sharePost("facebook")}
                            className="cursor-pointer"
                          >
                            <FaFacebookF className="w-4 h-4 mr-2 text-blue-600" />
                            Facebook
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => sharePost("twitter")}
                            className="cursor-pointer"
                          >
                            <FaTwitter className="w-4 h-4 mr-2 text-blue-400" />
                            Twitter
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => sharePost("whatsapp")}
                            className="cursor-pointer"
                          >
                            <FaWhatsapp className="w-4 h-4 mr-2 text-green-600" />
                            WhatsApp
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => sharePost("copy")}
                            className="cursor-pointer"
                          >
                            <FaCopy className="w-4 h-4 mr-2 text-muted-foreground" />
                            {copied ? "Copied!" : "Copy Link"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>

                {/* Post Content - This will be passed as children */}
                <div className="prose prose-lg max-w-none">{children}</div>

                {/* Tags - Not shown for pages */}
                {post.type !== "page" && post.tags && post.tags.length > 0 && (
                  <div className="mt-6 py-4 border-y border-border flex items-center">
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="text-xs border-primary/30 text-primary hover:bg-primary/10 transition-colors"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Related Posts - Not shown for pages */}
                {post.type !== "page" && (
                  <RelatedPosts
                    relatedPosts={relatedPosts}
                    formatDate={formatDate}
                  />
                )}
              </div>
            </article>
          </div>

          {/* Sidebar */}
          <Sidebar />
        </div>
      </div>
    </div>
  );
}
